import React, { useEffect, useState } from 'react';

import { Template } from '@dev/ee-api/ee-api-models';
import { push as Push, replace as Replace } from 'redux-first-history';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation, withTranslation } from 'react-i18next';
import { compose } from 'react-recompose';

import { TemplateList as TemplateListModule } from '@dev/ee-modules';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

import { useDashboardElementVisibility } from '@/services/hooks/useDashboardElementVisibility';
import { getLandingPageCreatorStore, LoadStart, Reset } from '@/store/landingPageCreator/actions';
import { getAccountAdvancedOptions } from '@/store/account/actions';
import { LandingPageCreatorStatus } from '@/store/landingPageCreator/types';
import { ShowDialog, UpdateDialog, HideDialog } from '../../store/dialog/actions';
import { ShowDialogParams } from '../../store/dialog/types';
import { LandingPageListProps, PreviewContent } from './LandingPageListInterface';
import {
    StartLoadingTemplates,
    StartLoadingTemplateParams,
    templateDeleteStart,
    templateArrayDeleteStart,
    StartUpdatingTemplateParams,
    templateUpdateStart,
    TemplatePreviewStart,
    AddTemplateStart,
    downloadLandingPageStart,
    getLandingPageList,
} from '../../store/landingPageList/actions';
import { LoadTemplate } from '../../store/raweditor/actions';
import { ApplicationState as State } from '../../store';
import Props from './LandingPageListInterface';
import { addParamToUrl } from '../../services/tools/handleUrlParams';
import { LandingPagePaths } from '../../services/dictionaries/Paths';
import { PreviewLandingPage } from './components/Preview';
import { getStepsValidity } from '../LandingPageCreator/tools/getStepsValidity';
import { Step5Content } from '../LandingPageCreator/steps/Step5Content';
import { LandingPagePublishUnavailable } from './components/LandingPagePublishUnavailable';
import { useLandingPageSubdomain } from '../LandingPageCreator/hooks/useLandingPageSubdomain';

declare const window: any;
declare const ee: any;

const mapState = (state: State, ownProps: LandingPageListProps): LandingPageListProps => {
    const getApiPath = (): string => {
        if (!state.config.isStandalone && window.ee) {
            return ee.api.path;
        }

        return APIPATH;
    };

    return {
        ...ownProps,
        config: state.config,
        account: state.account,
        browser: state.browser,
        templates: state.landingPageList,
        rawEditor: state.rawEditor,
        APIPATH: getApiPath(),
        isMarketing: true,
        isLandingPage: true,
    };
};

interface DispatchProps {
    StartLoadingTemplates(params: StartLoadingTemplateParams): void;
    ShowDialog: (params: ShowDialogParams) => void;
    HideDialog: () => void;
    UpdateDialog: (params: ShowDialogParams) => void;
    templateDeleteStart: (name: string, params: StartLoadingTemplateParams) => void;
    templateArrayDeleteStart: (templatesToDelete: string[], params: StartLoadingTemplateParams) => void;
    TemplatePreviewStart: (templateName: string, ispublic?: boolean) => void;
    templateUpdateStart(params: StartUpdatingTemplateParams): void;
    AddTemplateStart: (template: Template) => void;
    LoadTemplate: (name: string, loadDefault: boolean, isPublic?: boolean) => void;
    Push: (url) => void;
    Replace(url: string | Record<string, string>): void;
    downloadLandingPageStart(name: string): void;
}
const dispatchProps = {
    StartLoadingTemplates,
    ShowDialog,
    HideDialog,
    UpdateDialog,
    templateDeleteStart,
    templateArrayDeleteStart,
    TemplatePreviewStart,
    templateUpdateStart,
    AddTemplateStart,
    LoadTemplate,
    Push,
    Replace,
    downloadLandingPageStart,
};

type ExtendedProps = LandingPageListProps & DispatchProps;


const LandingPageList = (props: ExtendedProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const setUrlParams = (param: Record<string, any>): void => {
        const { Replace } = props;
        const urlParams = addParamToUrl(param);
        Replace({ search: urlParams });
    };
    const paths = { ...LandingPagePaths };
    const [openPreview, setOpenPreview] = useState(false);
    const [openPublish, setOpenPublish] = useState(false);
    const [previewContent, setPreviewContent] = useState<PreviewContent>(null);
    const { template, name, PreviewLoading } = useSelector(getLandingPageList);
    const advancedOptions = useSelector(getAccountAdvancedOptions);
    const {landingPageSubdomain} = useLandingPageSubdomain()
    const { landingPageObject, templateObject, landingPageMetaDataObject, status: LandingPageStatus } = useSelector(getLandingPageCreatorStore);

    useEffect(() => {
        if (PreviewLoading) {
            return;
        }
        setPreviewContent({
            name,
            content: template as string,
        });
    },[PreviewLoading]);

    useEffect(() => {
        if (LandingPageStatus !== LandingPageCreatorStatus.LOADED || !openPublish || (landingPageSubdomain?.LandingPageID && landingPageSubdomain?.LandingPageID !== landingPageObject.LandingPageID) ) {
            return;
        }

        const isStep3Visible = !advancedOptions?.IsOwnedByReseller;
        const landingPageValidity = getStepsValidity(isStep3Visible, landingPageObject, templateObject, landingPageMetaDataObject,landingPageSubdomain);
        const isLandingPageValid = landingPageValidity[5];


        const body = isLandingPageValid ? <Step5Content /> : <LandingPagePublishUnavailable />;

        const params: ShowDialogParams = {
            title: t('publish'),
            cancelLabel: t('close'),
            
            body,
            width: '870px',
            onClickCancel: () => {
                dispatch(Reset())
                setOpenPublish(false)
            },
        };

        props.ShowDialog(params);
    }, [LandingPageStatus,landingPageSubdomain?.LandingPageID])

    const onPreviewClick = (): void => {
        setOpenPreview(true);
    };

    const onPreviewClose = (): void => {
        setPreviewContent(null);
        setOpenPreview(false);
    };

    const onPublishClick = (name: string) => {
        dispatch(LoadStart(safelyDecodeBase64Url(name)));
        setOpenPublish(true);
    };
    const { linkInBio: { isLinkInBioInsteadLandingPageVisible }} = useDashboardElementVisibility();

    return (
        <>
            <TemplateListModule
                paths={paths}
                retriveUrlParamsToAdd={setUrlParams}
                onPreviewClick={onPreviewClick}
                onPublishClick={onPublishClick}
                isLinkInBio={isLinkInBioInsteadLandingPageVisible}
                hideTemplateType={isLinkInBioInsteadLandingPageVisible}
                {...props}
            />
            {open && !PreviewLoading && (
                <PreviewLandingPage
                    open={openPreview}
                    name={previewContent?.name}
                    previewContent={previewContent?.content}
                    onClose={onPreviewClose}
                />
            )}
        </>
    );
}

export default compose(withTranslation(), connect<Props, DispatchProps>(mapState, dispatchProps))(LandingPageList);
